import React from 'react';
import { cn } from '../../utils/helpers';

/* ── Button ──────────────────────────────────────────────────────────────────── */
export const Button = ({ variant = 'primary', size = 'md', loading, children, className, style, ...props }) => {
  const base = 'inline-flex items-center justify-center gap-2 font-medium transition-all duration-150 rounded-md disabled:opacity-40 disabled:cursor-not-allowed';
  const variants = {
    primary:  'bg-[var(--cyan)] hover:brightness-110 active:scale-95',
    secondary:'bg-[var(--bg-elevated)] border border-[var(--border-light)] hover:bg-[var(--bg-hover)]',
    ghost:    'hover:bg-[var(--bg-hover)]',
    danger:   'bg-[var(--red-dim)] border border-[var(--red)] border-opacity-30 hover:bg-[var(--red)] hover:text-white',
  };
  const textColors = {
    primary: '#ffffff',
    secondary: '#ffffff',
    ghost: 'var(--text-secondary)',
    danger: 'var(--red)',
  };
  const sizes = {
    sm: 'px-3 py-1.5 text-xs',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
  };
  return (
    <button 
      className={cn(base, variants[variant], sizes[size], className)} 
      style={{ color: textColors[variant] || '#ffffff', ...style }}
      disabled={loading || props.disabled} 
      {...props}
    >
      {loading && <Spinner size="sm" />}
      {children}
    </button>
  );
};

/* ── Input ───────────────────────────────────────────────────────────────────── */
export const Input = React.forwardRef(({ label, error, icon, className, ...props }, ref) => (
  <div className="flex flex-col gap-1.5">
    {label && <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</label>}
    <div style={{ position: 'relative' }}>
      {icon && <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', display: 'flex' }}>{icon}</span>}
      <input
        ref={ref}
        style={{
          width: '100%',
          padding: icon ? '10px 12px 10px 38px' : '10px 12px',
          background: 'var(--bg-elevated)',
          border: `1px solid ${error ? 'var(--red)' : 'var(--border-light)'}`,
          borderRadius: 'var(--radius-md)',
          color: 'var(--text-primary)',
          fontSize: '0.9rem',
          transition: 'border-color 150ms',
        }}
        className={className}
        onFocus={(e) => { e.target.style.borderColor = error ? 'var(--red)' : 'var(--cyan)'; }}
        onBlur={(e) => { e.target.style.borderColor = error ? 'var(--red)' : 'var(--border-light)'; }}
        {...props}
      />
    </div>
    {error && <span style={{ fontSize: '0.75rem', color: 'var(--red)' }}>{error}</span>}
  </div>
));
Input.displayName = 'Input';

/* ── Textarea ────────────────────────────────────────────────────────────────── */
export const Textarea = React.forwardRef(({ label, error, className, ...props }, ref) => (
  <div className="flex flex-col gap-1.5">
    {label && <label style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</label>}
    <textarea
      ref={ref}
      style={{
        width: '100%',
        padding: '10px 12px',
        background: 'var(--bg-elevated)',
        border: `1px solid ${error ? 'var(--red)' : 'var(--border-light)'}`,
        borderRadius: 'var(--radius-md)',
        color: 'var(--text-primary)',
        fontSize: '0.9rem',
        resize: 'vertical',
        minHeight: 120,
      }}
      className={className}
      {...props}
    />
    {error && <span style={{ fontSize: '0.75rem', color: 'var(--red)' }}>{error}</span>}
  </div>
));
Textarea.displayName = 'Textarea';

/* ── Badge ───────────────────────────────────────────────────────────────────── */
export const Badge = ({ children, variant = 'default', size = 'sm' }) => {
  const variants = {
    default:   { bg: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: 'var(--border)' },
    cyan:      { bg: 'var(--cyan-dim)',    color: 'var(--cyan)',            border: 'rgba(0,225,200,0.2)' },
    green:     { bg: 'var(--green-dim)',   color: 'var(--green)',           border: 'rgba(61,255,160,0.2)' },
    red:       { bg: 'var(--red-dim)',     color: 'var(--red)',             border: 'rgba(255,77,109,0.2)' },
    amber:     { bg: 'var(--amber-dim)',   color: 'var(--amber)',           border: 'rgba(245,166,35,0.2)' },
  };
  const { bg, color, border } = variants[variant] || variants.default;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: size === 'sm' ? '2px 8px' : '4px 12px',
      fontSize: size === 'sm' ? '0.7rem' : '0.8rem',
      fontFamily: 'var(--font-mono)',
      fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase',
      borderRadius: 'var(--radius-sm)',
      background: bg, color, border: `1px solid ${border}`,
    }}>
      {children}
    </span>
  );
};

/* ── Spinner ─────────────────────────────────────────────────────────────────── */
export const Spinner = ({ size = 'md' }) => {
  const s = { sm: 14, md: 20, lg: 36 }[size];
  return (
    <span style={{
      display: 'inline-block',
      width: s, height: s,
      border: `2px solid var(--border)`,
      borderTopColor: 'var(--cyan)',
      borderRadius: '50%',
      animation: 'spin 0.7s linear infinite',
    }} />
  );
};

/* ── Card ────────────────────────────────────────────────────────────────────── */
export const Card = ({ children, className, style, ...props }) => (
  <div
    style={{
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      ...style,
    }}
    className={cn('animate-in', className)}
    {...props}
  >
    {children}
  </div>
);

/* ── StatCard ─────────────────────────────────────────────────────────────────── */
export const StatCard = ({ label, value, sub, accent, icon, delta }) => (
  <Card style={{ padding: '20px 24px' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: '0.7rem', fontFamily: 'var(--font-mono)', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: '2rem', fontFamily: 'var(--font-mono)', fontWeight: 700, color: accent || 'var(--text-primary)', lineHeight: 1 }}>{value ?? '—'}</p>
        {sub && <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 6 }}>{sub}</p>}
        {delta !== undefined && (
          <p style={{ fontSize: '0.75rem', color: delta >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
            {delta >= 0 ? '▲' : '▼'} {Math.abs(delta)}%
          </p>
        )}
      </div>
      {icon && <span style={{ fontSize: '1.4rem', opacity: 0.5 }}>{icon}</span>}
    </div>
  </Card>
);

/* ── EmptyState ───────────────────────────────────────────────────────────────── */
export const EmptyState = ({ icon = '📭', title, description, action }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 24px', gap: 16, textAlign: 'center' }}>
    <span style={{ fontSize: '3rem' }}>{icon}</span>
    <div>
      <p style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>{title}</p>
      <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', maxWidth: 300 }}>{description}</p>
    </div>
    {action}
  </div>
);

/* ── Skeleton ─────────────────────────────────────────────────────────────────── */
export const Skeleton = ({ width = '100%', height = 16, rounded = false }) => (
  <div style={{
    width, height,
    borderRadius: rounded ? '50%' : 'var(--radius-sm)',
    background: 'linear-gradient(90deg, var(--bg-elevated) 25%, var(--bg-hover) 50%, var(--bg-elevated) 75%)',
    backgroundSize: '400px 100%',
    animation: 'shimmer 1.4s infinite',
  }} />
);

/* ── Divider ──────────────────────────────────────────────────────────────────── */
export const Divider = ({ label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0' }}>
    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
    {label && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', letterSpacing: '0.08em', whiteSpace: 'nowrap' }}>{label}</span>}
    <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
  </div>
);

/* ── Modal ───────────────────────────────────────────────────────────────────── */
export const Modal = ({ open, onClose, title, children, width = 520 }) => {
  if (!open) return null;
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 999,
        background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-surface)',
          border: '1px solid var(--border-light)',
          borderRadius: 'var(--radius-xl)',
          width: '100%', maxWidth: width,
          maxHeight: '90vh', overflowY: 'auto',
          animation: 'fadeIn 0.2s ease',
        }}
      >
        {title && (
          <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>{title}</h3>
            <button onClick={onClose} style={{ color: 'var(--text-muted)', fontSize: '1.2rem', cursor: 'pointer', background: 'none', border: 'none', lineHeight: 1 }}>✕</button>
          </div>
        )}
        <div style={{ padding: 24 }}>{children}</div>
      </div>
    </div>
  );
};

/* ── Tooltip ─────────────────────────────────────────────────────────────────── */
export const Tooltip = ({ children, text }) => (
  <div style={{ position: 'relative', display: 'inline-flex' }} className="group">
    {children}
    <div style={{
      position: 'absolute', bottom: '120%', left: '50%', transform: 'translateX(-50%)',
      background: 'var(--bg-hover)', border: '1px solid var(--border-light)',
      borderRadius: 'var(--radius-sm)', padding: '4px 10px',
      fontSize: '0.72rem', color: 'var(--text-secondary)', whiteSpace: 'nowrap',
      pointerEvents: 'none', opacity: 0, transition: 'opacity 150ms',
      zIndex: 100,
    }} className="tooltip-text">{text}</div>
  </div>
);
